import React, { ReactElement, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { ChargePointImport, EarthingSystem } from '../../../common/redux/api/web-api'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'
import { Combobox } from '../../../common/components/combobox'
import Input from '../../../common/components/Input'
import { transformLBCurrentInput } from '../../../common/features/loadbalancer-group/loadbalancer-group-modal'
import { SFormRow } from '../../../common/styles/Form'
import { InfoIcon } from '../../../lib/icons/info-icon'
import { isChargingConnectorImport } from '../../../lib/findChargingConnector'
import { isHaloImport } from '../../../lib/isHalo'
import { CHARGEPOINT_MIN_CURRENT } from '../../../common/constants/ChargePointConstants'
import { isAuraImport } from '../../../lib/isAura'
import { getChargerPhaseListByImport } from '../../../common/utils/chargePointUtils'

type ChargePointSettingsType = {
    singleCharger: boolean
    chargePoint: ChargePointImport
    chargerType?: string
    minInstallationCurrent?: number
    earthingSystem?: EarthingSystem | null
}

export default ({
    singleCharger,
    chargePoint,
    minInstallationCurrent,
    chargerType,
    earthingSystem,
}: ChargePointSettingsType): ReactElement => {
    const text = useText()

    const connector = chargePoint?.connectors?.find((connector) => connector?.connectorId === 1)
    const installationPhaseMode = useMemo(() => connector?.installationPhaseMode, [connector])

    const phasesList = getChargerPhaseListByImport(chargePoint, earthingSystem, installationPhaseMode)
    const phasesOptions = phasesList.map((phase) => ({ value: phase, label: phase }))

    const {
        control,
        register,
        setValue,
        formState: { errors },
    } = useFormContext()

    return (
        <>
            <div>
                {chargePoint &&
                    chargePoint?.connectors
                        ?.filter(
                            (connector) =>
                                (isChargingConnectorImport(connector) && !isHaloImport(chargePoint)) ||
                                (isHaloImport(chargePoint) && connector.connectorId === 1)
                        )
                        ?.map(
                            (connector) =>
                                connector && (
                                    <span key={connector?.connectorId}>
                                        {isAuraImport(chargePoint) && (
                                            <div className=" mt-4">
                                                {connector?.connectorId === 1 ? (
                                                    text('lblLeftSocket')
                                                ) : (
                                                    <>{text('lblRightSocket')}</>
                                                )}
                                            </div>
                                        )}
                                        <div className="grid mt-4">
                                            <div className="col-12 lg:col-6">
                                                <SFormRow>
                                                    <div
                                                        className=" flex gap-2 mb-3 col-12"
                                                        data-testid={'lbOfflineCurrent-field-' + connector.name}
                                                    >
                                                        <Text code="lblLoadBalancerOfflineCurrentLimitField" />

                                                        <InfoIcon
                                                            title={text('msgLoadBalancerOfflineCurrentLimitField')}
                                                            size={18}
                                                        />
                                                    </div>

                                                    <Controller
                                                        name={
                                                            `lbc_${connector?.connectorId}_${chargerType}` ?? undefined
                                                        }
                                                        control={control}
                                                        render={({ field: { onChange, value } }) => (
                                                            <Input
                                                                id={`lbc_${connector?.connectorId}_${chargerType}`}
                                                                type="number"
                                                                required
                                                                {...register(
                                                                    `lbc_${connector?.connectorId}_${chargerType}`,
                                                                    {
                                                                        required: {
                                                                            value: true,
                                                                            message: text('msgLBOfflineCurrent'),
                                                                        },
                                                                        max: {
                                                                            value: minInstallationCurrent as number,
                                                                            message: text(
                                                                                'lblInstallationCurrentOfflineValidationMax',
                                                                                {
                                                                                    connectorType:
                                                                                        connector?.connectorId == 1
                                                                                            ? chargePoint?.connectors &&
                                                                                              chargePoint?.connectors[0]
                                                                                                  ?.name
                                                                                            : chargePoint?.connectors &&
                                                                                              chargePoint?.connectors[1]
                                                                                                  ?.name,
                                                                                    installationCurrent:
                                                                                        minInstallationCurrent,
                                                                                }
                                                                            ),
                                                                        },
                                                                        min: {
                                                                            value: CHARGEPOINT_MIN_CURRENT,
                                                                            message: text(
                                                                                'lblInstallationCurrentOfflineValidation',
                                                                                {
                                                                                    connectorType:
                                                                                        connector?.connectorId == 1
                                                                                            ? chargePoint?.connectors &&
                                                                                              chargePoint?.connectors[0]
                                                                                                  ?.name
                                                                                            : chargePoint?.connectors &&
                                                                                              chargePoint?.connectors[1]
                                                                                                  ?.name,
                                                                                    minCurrent: CHARGEPOINT_MIN_CURRENT,
                                                                                }
                                                                            ),
                                                                        },
                                                                    }
                                                                )}
                                                                data-testid={
                                                                    'lbOfflineCurrent-input-' +
                                                                    connector?.name +
                                                                    connector?.connectorId
                                                                }
                                                                onChange={(event) => {
                                                                    const transformedValue = parseInt(
                                                                        event.target.value
                                                                    )

                                                                    onChange(
                                                                        transformLBCurrentInput.input(transformedValue)
                                                                    )
                                                                    setValue(
                                                                        `lbc_${connector?.connectorId}_${chargerType}`,
                                                                        event.target.value
                                                                    )
                                                                }}
                                                                error={
                                                                    errors[
                                                                        `lbc_${connector?.connectorId}_${chargerType}`
                                                                    ]?.message as string
                                                                }
                                                                value={value}
                                                            />
                                                        )}
                                                    />
                                                </SFormRow>
                                            </div>

                                            <div className="col-12 lg:col-6">
                                                <SFormRow>
                                                    <div
                                                        className=" flex gap-2 mb-3 col-12"
                                                        data-testid={'lbOfflinePhase-field-' + connector?.name}
                                                    >
                                                        <Text code="lblLoadBalancerOfflinePhase" />

                                                        <InfoIcon
                                                            title={text('msgLoadBalancerOfflinePhase')}
                                                            size={18}
                                                        />
                                                    </div>

                                                    <div
                                                        className="mb-6"
                                                        data-testid={'lbOfflinePhase-field-' + connector?.name}
                                                    >
                                                        <Controller
                                                            name={`lbOfflinePhase_${connector?.connectorId}`}
                                                            control={control}
                                                            render={({ field: { value, onChange } }) => (
                                                                <Combobox
                                                                    {...register(
                                                                        `lbOfflinePhase_${connector?.connectorId}`
                                                                    )}
                                                                    enableDefaultOption={false}
                                                                    data-testid={`lbOfflinePhase_${connector.name}`}
                                                                    id={`lbOfflinePhase_${connector?.connectorId}`}
                                                                    disabled={!singleCharger}
                                                                    options={phasesOptions}
                                                                    value={value}
                                                                    onChange={(option) => {
                                                                        onChange(option.value)
                                                                        setValue(
                                                                            `lbOfflinePhase_${connector?.connectorId}`,
                                                                            option.value
                                                                        )
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </SFormRow>
                                            </div>
                                        </div>
                                    </span>
                                )
                        )}
            </div>
        </>
    )
}
